// The VIEW & CADENCE and APPROVERS steps (prototype stepBody 'view'/'approvers'). The board-axis
// picker is the DERIVATION display (brief §3.1 view row): every option renders its shape line and
// its derivation sentence from the projection VERBATIM — the agent's pick carries its one-sentence
// WHY, a non-picked available axis notes it is one tab away (with the declaration that unlocked
// it), and an unavailable axis states the declaration it still needs — honest, never a disabled
// mystery. Availability and recommendation are PROJECTION flags, never decided here (G-182).
// Cadence renders as chips (a field, not a Schedule). Approvers render the platform approver set
// the projection carries. Machine hooks: data-view/data-selected/data-shape/data-derivation/
// data-note on options; chips carry data-chip (G-180 test seams).

import type { ComposerViewOption, WorkView } from '../../projections.ts';
import { Accent, ConfigChip, FieldLabel, WhyLine } from '../composer-ui.tsx';
import { axisNote } from '../view-derivation.ts';

export interface ViewCadenceStepProps {
  readonly options: readonly ComposerViewOption[];
  readonly activeView: WorkView;
  readonly onPick: (view: WorkView) => void;
  readonly cadenceLabel: string;
  readonly timezone: string;
  readonly boardCap: number;
}

function ViewOption({
  option,
  selected,
  onPick,
}: {
  readonly option: ComposerViewOption;
  readonly selected: boolean;
  readonly onPick: (view: WorkView) => void;
}): React.JSX.Element {
  const frame = selected ? 'border-forest bg-forest-tint' : 'border-border bg-surface';
  const note = axisNote(option);
  return (
    <button
      type="button"
      data-view={option.view}
      data-selected={selected}
      disabled={!option.available}
      aria-pressed={selected}
      onClick={() => onPick(option.view)}
      className={`min-w-0 flex-1 rounded-md border p-4 text-left disabled:opacity-60 ${frame}`}
    >
      <span className="flex items-center gap-2 text-sm font-semibold text-text">
        {option.title}
        {option.recommended ? (
          <span className="ritona-numeric rounded-sm border border-sage bg-surface px-1.5 py-0.5 text-xs uppercase text-forest">
            agent’s pick
          </span>
        ) : null}
      </span>
      <span
        data-shape={option.view}
        className="ritona-numeric mt-1.5 block text-xs uppercase tracking-eyebrow text-text-subtle"
      >
        {option.shape}
      </span>
      <span
        data-derivation={option.recommended ? 'pick' : 'axis'}
        className="mt-2 block text-sm leading-relaxed text-text-muted"
      >
        {option.summary}
      </span>
      {note !== null ? (
        <span data-note={note.kind} className="mt-2 block text-xs leading-relaxed text-text-subtle">
          {note.text}
        </span>
      ) : null}
    </button>
  );
}

/** The board-axis picker plus the sweep cadence chips. */
export function ViewCadenceStep(props: ViewCadenceStepProps): React.JSX.Element {
  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row">
        {props.options.map((option) => (
          <ViewOption
            key={option.view}
            option={option}
            selected={option.view === props.activeView}
            onPick={props.onPick}
          />
        ))}
      </div>
      <div className="mt-6">
        <FieldLabel>Cadence</FieldLabel>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <ConfigChip hook="cadence">{props.cadenceLabel}</ConfigChip>
          <ConfigChip hook="cadence">{props.timezone}</ConfigChip>
          <ConfigChip hook="cadence">{`board cap ~${String(props.boardCap)}`}</ConfigChip>
        </div>
      </div>
      <WhyLine>
        A field, not a Schedule — the one platform sweep job reads it and runs what’s due.{' '}
        <Accent>No second object to keep in sync.</Accent>
      </WhyLine>
    </div>
  );
}

/** The approver set that can clear this workflow's gates (per-definition sets arrive later). */
export function ApproversStep({
  approvers,
}: {
  readonly approvers: readonly string[];
}): React.JSX.Element {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {approvers.map((approver) => (
          <ConfigChip key={approver} hook="approver">
            {approver}
          </ConfigChip>
        ))}
        <ConfigChip hook="approver" variant="dead">
          per-workflow approver sets · later
        </ConfigChip>
      </div>
      <WhyLine>
        Who can clear this workflow’s gates. In v1 the platform approver set applies; per-workflow
        sets come later.
      </WhyLine>
    </div>
  );
}
